import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { Observable, tap, catchError, of, finalize } from 'rxjs';
import { AuthStateService } from '@dental-ui/auth';
import { UserBranchApiService } from '../lib/data-access/user-branch-api.service';
import { ResponseModel } from '@dental-ui/account/models';
import { UserBranchDto } from '../lib/models';

@Injectable({
  providedIn: 'root',
})
export class UserBranchStateService {
  private readonly apiService = inject(UserBranchApiService);
  private readonly authState = inject(AuthStateService);
  
  constructor() {
    // Auth durumu false olduğunda (logout) state'i temizle
    effect(() => {
      if (!this.authState.isAuthenticated()) {
        this.clear();
      }
    });
  }

  // Signals
  private readonly _userBranches = signal<UserBranchDto[]>([]);
  private readonly _isLoading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  // Public computed signals
  readonly userBranches = computed(() => this._userBranches());
  readonly isLoading = computed(() => this._isLoading());
  readonly error = computed(() => this._error());

  /**
   * Tüm kullanıcı şubelerini yükler
   */
  loadAllUserBranches(): Observable<ResponseModel<UserBranchDto[]>> {
    this._isLoading.set(true);
    this._error.set(null);

    return this.apiService.getAllUserBranches().pipe(
      tap((response: ResponseModel<UserBranchDto[]>) => {
        if (response.success && response.data) {
          this._userBranches.set(response.data || []);
        } else {
          this.handleError(
            response.message || 'Failed to load user branches'
          );
        }
      }),
      catchError((error: unknown) => {
        const errorMessage =
          error instanceof Error ? error.message : 'An error occurred';
        this.handleError(errorMessage);
        return of({
          success: false,
          message: errorMessage,
          data: [],
        } as ResponseModel<UserBranchDto[]>);
      }),
      finalize(() => {
        this._isLoading.set(false);
      })
    );
  }

  clearError(): void {
    this._error.set(null);
  }

  /**
   * Tüm profil durumlarını temizle (logout helper)
   */
  clear(): void {
    this._userBranches.set([]);
    this._isLoading.set(false);
    this._error.set(null);
  }

  private handleError(message: string): void {
    this._error.set(message);
    this._userBranches.set([]);
  }
}

